import { useState, useEffect, useRef } from 'react';
import {
  generateSummary,
  extractKeyPoints,
  detectActionItems,
  answerQuestion,
  isAIConfigured,
} from '@/services/aiService';

export interface ActionItem {
  owner: string;
  task: string;
  deadline?: string;
}

export interface Question {
  text: string;
  askedBy: string;
}

export interface OrganizerContext {
  className?: string;
  assignments?: Array<{
    title: string;
    dueDate: string;
    status: string;
  }>;
  upcomingDeadlines?: string[];
  recentMessages?: string[];
}

export interface AIAssistantState {
  // Session tracking
  sessionDuration: number;
  sessionStartTime: Date | null;
  participantCount: number;
  participantNames: string[];
  
  // Content collection
  transcriptText: string;
  chatMessages: string[];
  
  // AI outputs
  summary: string;
  keyPoints: string[];
  actionItems: ActionItem[];
  questions: Question[];
  
  // State
  isProcessing: boolean;
  useAI: boolean; // Whether to use OpenAI or rule-based
  
  // Methods
  updateTranscript: (text: string) => void;
  addChatMessage: (message: string) => void;
  askQuestion: (question: string) => Promise<string>;
  refreshAI: () => Promise<void>;
  startSession: () => void;
  endSession: () => void;
}

export const useAIAssistant = (
  roomId: string,
  initialParticipants: string[] = [],
  organizerContext?: OrganizerContext
): AIAssistantState => {
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [participantNames, setParticipantNames] = useState<string[]>(initialParticipants);
  const [transcriptText, setTranscriptText] = useState('');
  const [chatMessages, setChatMessages] = useState<string[]>([]);
  const [summary, setSummary] = useState('');
  const [keyPoints, setKeyPoints] = useState<string[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [useAI, setUseAI] = useState(isAIConfigured());
  const [lastAIUpdate, setLastAIUpdate] = useState<number>(0);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const aiUpdateTimer = useRef<NodeJS.Timeout | null>(null);

  // Session timer
  useEffect(() => {
    if (sessionStartTime) {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartTime.getTime()) / 1000);
        setSessionDuration(elapsed);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sessionStartTime]);

  // Auto-analyze transcript for simple patterns (Phase 2 - Rule-based)
  useEffect(() => {
    if (transcriptText.length > 100) {
      if (useAI && transcriptText.length > 300) {
        // Use AI every 5 minutes or when significant content added
        const now = Date.now();
        if (now - lastAIUpdate > 300000) { // 5 minutes
          refreshAI();
        }
      } else {
        // Fallback to rule-based
        analyzeTranscriptSimple(transcriptText);
      }
    }
  }, [transcriptText, useAI, lastAIUpdate]);

  const analyzeTranscriptSimple = (text: string) => {
    // Simple rule-based analysis without AI API
    
    // Check for due date mentions in organizer context
    if (organizerContext?.upcomingDeadlines && organizerContext.upcomingDeadlines.length > 0) {
      const deadline = organizerContext.upcomingDeadlines[0];
      const currentSummary = summary || '';
      if (!currentSummary.includes(deadline)) {
        setSummary(currentSummary + ` ⚠️ Reminder: ${deadline}`);
      }
    }
    
    // Detect questions (sentences ending with ?)
    const questionMatches = text.match(/([^.!?]+\?)/g);
    if (questionMatches) {
      const detectedQuestions = questionMatches
        .filter(q => q.length > 10) // Filter out short questions
        .slice(-5) // Keep last 5
        .map(q => ({
          text: q.trim(),
          askedBy: 'Unknown', // TODO: Parse speaker
        }));
      setQuestions(detectedQuestions);
    }

    // Detect action items (patterns like "will", "should", "need to")
    const actionPatterns = [
      /(\w+)\s+will\s+(.+?)(?:[.!?]|$)/gi,
      /(\w+)\s+should\s+(.+?)(?:[.!?]|$)/gi,
      /(\w+)\s+needs? to\s+(.+?)(?:[.!?]|$)/gi,
    ];

    const detectedActions: ActionItem[] = [];
    actionPatterns.forEach(pattern => {
      const matches = [...text.matchAll(pattern)];
      matches.slice(-3).forEach(match => {
        detectedActions.push({
          owner: match[1],
          task: match[2].trim(),
        });
      });
    });
    setActionItems(detectedActions.slice(0, 5)); // Keep top 5

    // Generate simple summary
    if (text.length > 200) {
      const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 20);
      const firstSentence = sentences[0]?.trim() || '';
      const lastSentence = sentences[sentences.length - 1]?.trim() || '';
      
      let baseSummary = 
        `Discussion in progress. Started with: "${firstSentence.substring(0, 80)}..." ` +
        `Recent topic: "${lastSentence.substring(0, 80)}..."`;
      
      // Add organizer context to summary
      if (organizerContext?.assignments && organizerContext.assignments.length > 0) {
        const upcomingAssignments = organizerContext.assignments
          .filter(a => a.status !== 'completed')
          .slice(0, 2);
        
        if (upcomingAssignments.length > 0) {
          baseSummary += `\n\n📚 Related: ${upcomingAssignments.map(a => 
            `${a.title} (Due: ${a.dueDate})`
          ).join(', ')}`;
        }
      }
      
      setSummary(baseSummary);
    } else {
      let initialSummary = 'Session starting... AI will summarize key points as discussion progresses.';
      
      // Show upcoming deadlines even at start
      if (organizerContext?.upcomingDeadlines && organizerContext.upcomingDeadlines.length > 0) {
        initialSummary += `\n\n⚠️ ${organizerContext.upcomingDeadlines.join(' • ')}`;
      }
      
      setSummary(initialSummary);
    }

    // Extract simple key points (sentences with keywords)
    const keywordPatterns = [
      'important',
      'key',
      'main',
      'focus',
      'remember',
      'conclusion',
      'result',
      'finding',
    ];
    
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 20);
    const importantSentences = sentences.filter(sentence =>
      keywordPatterns.some(keyword => sentence.toLowerCase().includes(keyword))
    );
    
    setKeyPoints(
      importantSentences
        .slice(-5)
        .map(s => s.trim().substring(0, 100))
    );
  };

  const refreshAI = async () => {
    if (!useAI || isProcessing || transcriptText.length < 300) {
      return;
    }

    setIsProcessing(true);
    console.log('[AI Assistant] Refreshing AI analysis...');

    try {
      // Run all AI operations in parallel
      const [aiSummary, aiKeyPoints, aiActionItems] = await Promise.all([
        generateSummary(transcriptText, undefined, {
          assignments: organizerContext?.assignments,
          upcomingDeadlines: organizerContext?.upcomingDeadlines,
        }),
        extractKeyPoints(transcriptText),
        detectActionItems(transcriptText),
      ]);

      setSummary(aiSummary);
      setKeyPoints(aiKeyPoints);
      setActionItems(aiActionItems);
      setLastAIUpdate(Date.now());

      console.log('[AI Assistant] AI analysis complete');
    } catch (error) {
      console.error('[AI Assistant] Error in AI analysis:', error);
      // Fallback to rule-based
      analyzeTranscriptSimple(transcriptText);
    } finally {
      setIsProcessing(false);
    }
  };

  const askQuestion = async (question: string): Promise<string> => {
    if (!useAI) {
      return 'AI Q&A requires OpenAI API key configuration. Using rule-based features only.';
    }

    setIsProcessing(true);
    try {
      const answer = await answerQuestion(question, transcriptText, chatMessages);
      return answer;
    } catch (error) {
      console.error('[AI Assistant] Error answering question:', error);
      return 'Unable to answer question. Please try again.';
    } finally {
      setIsProcessing(false);
    }
  };

  const startSession = () => {
    setSessionStartTime(new Date());
    console.log('[AI Assistant] Session started');
  };

  const endSession = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    console.log('[AI Assistant] Session ended. Duration:', sessionDuration, 'seconds');
    // TODO: Save to database
  };

  const updateTranscript = (text: string) => {
    setTranscriptText(prev => {
      // Append new text, keep last 5000 characters to avoid memory issues
      const combined = prev + ' ' + text;
      return combined.slice(-5000);
    });
  };

  const addChatMessage = (message: string) => {
    setChatMessages(prev => [...prev, message].slice(-50)); // Keep last 50 messages
  };

  // Update participants
  useEffect(() => {
    setParticipantNames(initialParticipants);
  }, [initialParticipants]);

  return {
    sessionDuration,
    sessionStartTime,
    participantCount: participantNames.length,
    participantNames,
    transcriptText,
    chatMessages,
    summary,
    keyPoints,
    actionItems,
    questions,
    isProcessing,
    useAI,
    updateTranscript,
    addChatMessage,
    askQuestion,
    refreshAI,
    startSession,
    endSession,
  };
};
